import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { AuthenticationService } from '@core/services/auth.service';
import { AccessStateService } from '@core/services/access-state.service';
import { PaymentService } from '@core/services/payment.service';
import { ProfileService } from '@core/services/profile.service';
import { ChatStateService } from '@core/services/chat-state.service';
import { AgentStateService } from '@core/services/agent-state.service';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { faEye, faEyeSlash, faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { AlertComponent } from '@shared/alert/alert.component';
import { LegalModalComponent } from '@shared/legal-modal/legal-modal.component';
import { convertUsernameToLowercase, convertInputToLowercase } from '@core/utils/form.utils';
import {
  buildRegisterPayload,
  createRegisterForm,
  extractRegisteredCompanyId,
  getPasswordRequirements,
} from '@core/utils/auth-register.utils';

@Component({
  selector: 'app-login',
  imports: [
    FontAwesomeModule,
    FormsModule,
    ReactiveFormsModule,
    AlertComponent,
    RouterModule,
    LegalModalComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('usernameInput') usernameInput!: ElementRef<HTMLInputElement>;
  @ViewChild('passwordInput') passwordInput!: ElementRef<HTMLInputElement>;
  
  loginForm: FormGroup;

  private subscriptions = new Subscription();

  show = false;
  toastTitle = '';
  toastMessage = '';
  toastType: 'info' | 'success' | 'warning' | 'alert' = 'info';
  
  isProcessingMicrosoftLogin = false;
  isMicrosoftLoginLoading = false;

  faMicrosoft = faMicrosoft;
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  faChevronRight = faChevronRight;
  faTimes = faTimes;

  showPassword = false;

  // ===== Recordar sesión =====
  rememberSession = false;

  // ===== Tab y Registro =====
  activeTab: 'login' | 'register' | 'forgot' = 'login';
  registerForm!: FormGroup;
  isRegistering = false;
  showRegisterPassword = false;
  showRegisterPasswordConfirm = false;

  // Legal modal
  showLegalModal = false;
  legalModalTitle = '';

  openLegalModal(event: Event, title: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.legalModalTitle = title;
    this.showLegalModal = true;
  }

  // ===== Forgot Password =====
  forgotPasswordForm!: FormGroup;
  isSendingRecovery = false;
  recoveryEmailSent = false;
  recoveryEmail = '';

  private toastTimer: any = null;
  // Track last focused field to restore focus if DOM changes steal it
  private lastFocusedField: 'username' | 'password' | null = null;
  // When we programmatically update the password control, suppress the
  // automatic focus-restore to avoid stealing focus from user actions.
  private suppressPasswordFocusRestore = false;
  // Keep references to DOM listeners so we can remove them on destroy
  private domListeners: { target: EventTarget; type: string; fn: EventListenerOrEventListenerObject }[] = [];

  constructor(
    private authService: AuthenticationService,
    private paymentService: PaymentService,
    private profileService: ProfileService,
    private accessStateService: AccessStateService,
    private chatStateService: ChatStateService,
    private agentStateService: AgentStateService,
    private router: Router,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });

    this.registerForm = createRegisterForm(this.formBuilder);

    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  switchTab(tab: 'login' | 'register' | 'forgot'): void {
    this.activeTab = tab;
  }

  // ===== Registro =====
  async register(): Promise<void> {
    if (this.registerForm.invalid) {
      Object.values(this.registerForm.controls).forEach(c => c.markAsTouched());
      return;
    }

    this.isRegistering = true;
    const registerData = buildRegisterPayload(this.registerForm, true);

    try {
      const registerResponse = await this.authService.register(registerData).toPromise();
      const companyId = extractRegisteredCompanyId(registerResponse);

      if (companyId) {
        const stripeCustomerId = registerResponse?.company?.stripe_customer_id;
        if (!stripeCustomerId) {
          await this.paymentService.createPaymentCustomer(companyId).toPromise();
        }
      }

      this.isRegistering = false;
      const userEmail = this.registerForm.get('email')?.value;
      this.router.navigate(['/verify-email'], { queryParams: { email: userEmail } });
    } catch (error: any) {
      this.isRegistering = false;
      const errorMessage = error?.error?.message || error?.message || 'Error al procesar el registro';
      this.showToast('alert', 'Error', errorMessage);
    }
  }

  convertRegisterToLowercase(event: any, fieldName: string): void {
    convertInputToLowercase(event, this.registerForm, fieldName);
  }

  getPasswordRequirements() {
    return getPasswordRequirements(this.registerForm.get('password')?.value || '');
  }

  toggleRegisterPassword(): void {
    this.showRegisterPassword = !this.showRegisterPassword;
  }

  toggleRegisterPasswordConfirm(): void {
    this.showRegisterPasswordConfirm = !this.showRegisterPasswordConfirm;
  }

  convertForgotToLowercase(event: any): void {
    convertInputToLowercase(event, this.forgotPasswordForm, 'email');
  }

  // ===== Forgot Password =====
  requestPasswordReset(): void {
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }

    this.isSendingRecovery = true;
    const email = this.forgotPasswordForm.get('email')?.value;
    this.recoveryEmail = email;

    this.authService.requestPasswordRecovery(email).subscribe({
      next: () => {
        this.isSendingRecovery = false;
        this.recoveryEmailSent = true;
        this.showToast('success', 'Correo enviado', 'Te hemos enviado un enlace de recuperación a tu correo electrónico');
      },
      error: (error: any) => {
        this.isSendingRecovery = false;
        const errorMessage = error?.error?.message || 'Ha ocurrido un error al enviar el correo de recuperación';
        this.showToast('alert', 'Error', errorMessage);
      }
    });
  }

  backToLoginFromForgot(): void {
    this.recoveryEmailSent = false;
    this.forgotPasswordForm.reset();
    this.switchTab('login');
  }

  ngOnInit(): void {
    // ===== RESTAURAR EMAIL RECORDADO =====
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      this.rememberSession = true;
      this.loginForm.patchValue({ username: rememberedEmail });
    }

    // ===== LIMPIEZA DE CACHÉS DE SESIÓN ANTERIOR =====
    // Garantiza que no queden datos residuales de otro usuario en los servicios singleton.
    // Esto cubre edge cases donde se llega al login por navegación SPA sin reload completo.
    this.profileService.clearAllCaches();
    this.accessStateService.clear();
    this.chatStateService.resetChat();
    this.chatStateService.clearAllStreams();
    this.agentStateService.resetAgents();

    // Obtener valores inmediatos para la limpieza inicial
    const hasBackofficeParam = this.route.snapshot.queryParams['backoffice'] === 'true';
    const hasBackofficeData = this.route.snapshot.data['backoffice'] === true;
    
    // ESTABLECER sessionStorage INMEDIATAMENTE si hay evidencia de backoffice
    if (hasBackofficeParam || hasBackofficeData) {
      sessionStorage.setItem('login_destination', 'backoffice');
    } else {
      sessionStorage.removeItem('login_destination');
    }
    
    // Detectar si viene de la ruta /backoffice (por si acaso, aunque ya se estableció arriba)
    this.route.data.subscribe((data) => {
      if (data['backoffice']) {
        sessionStorage.setItem('login_destination', 'backoffice');
      }
    });

    this.route.queryParams.subscribe((params) => {
      const accessToken = params['access_token'];
      const registered = params['registered'];
      const passwordReset = params['passwordReset'];
      const message = params['message'];
      const isBackoffice = params['backoffice'] === 'true';
      
      // Confirmar backoffice por query param (por si acaso, aunque ya se estableció arriba)
      if (isBackoffice) {
        sessionStorage.setItem('login_destination', 'backoffice');
      }
      
      if (accessToken) {
        this.isProcessingMicrosoftLogin = true;
        if (accessToken == 'error') {
          this.isProcessingMicrosoftLogin = false;
          this.showToast('alert', 'Error', 'El usuario de acceso no es válido');
        } else {
          this.handleTokenLogin(accessToken);
        }
      } else if (registered === 'true' && message) {
        this.showToast('success', 'Éxito', message);
        this.router.navigate(['/'], { replaceUrl: true });
      } else if (passwordReset === 'true' && message) {
        this.showToast('success', 'Contraseña actualizada', message);
        this.router.navigate(['/'], { replaceUrl: true });
      }
    });

    // Subscribe to password changes to restore focus if it was lost while typing
    const passwordControl = this.loginForm.get('password');
    if (passwordControl) {
      this.subscriptions.add(
        passwordControl.valueChanges.subscribe(() => {
          // If the user was typing in the password and focus moved away (e.g. due to DOM reflow), restore it
          try {
            // Do not attempt to restore focus if suppression flag is set
            if (this.suppressPasswordFocusRestore) {
              return;
            }
            const pwdEl = this.passwordInput?.nativeElement;
            // Only restore focus if the password input was the last focused
            // field and no other element currently has an active, editable focus
            if (this.lastFocusedField === 'password' && pwdEl) {
              const active = document.activeElement as HTMLElement | null;
              const activeIsInput = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable);
              // If some other input already has focus, do not steal it
              if (!activeIsInput || active === pwdEl) {
                // Use setTimeout to run after Angular updates the DOM
                setTimeout(() => {
                  try {
                    pwdEl.focus();
                    // Move cursor to end
                    const len = pwdEl.value?.length || 0;
                    pwdEl.setSelectionRange(len, len);
                  } catch (e) {
                    // ignore
                  }
                }, 0);
              }
            }
          } catch (e) {
            // safe
          }
        })
      );
    }
  }

  ngAfterViewInit(): void {
    // Detectar valores autocompletados por el navegador después de que la vista se inicialice
    setTimeout(() => {
      this.checkAndSyncAutocompleteValues();

      // Si el input username tiene autofocus nativo, quitarlo para evitar que
      // el navegador lo vuelva a enfocar (causa común en iOS).
      try {
        const userEl = this.usernameInput?.nativeElement;
        if (userEl && userEl.hasAttribute && userEl.hasAttribute('autofocus')) {
          userEl.removeAttribute('autofocus');
        }
      } catch (e) {
        // safe
      }
    }, 100);

    // Escuchar el evento 'change' para detectar autocomplete y mejorar tracking de foco
    if (this.usernameInput?.nativeElement) {
      const userEl = this.usernameInput.nativeElement;
      userEl.addEventListener('change', () => {
        this.syncInputValue('username');
      });

      userEl.addEventListener('animationstart', (e: AnimationEvent) => {
        if (e.animationName === 'onAutoFillStart') {
          this.syncInputValue('username');
        }
      });

      // Track focus on username (kept for compatibility)
      const onUsernameFocus = () => {
        this.lastFocusedField = 'username';
      };
      userEl.addEventListener('focus', onUsernameFocus);

      // iOS sometimes focuses after touchstart; register touch/pointer to capture intent
      const onUserTouch = () => {
        this.lastFocusedField = 'username';
      };
      userEl.addEventListener('touchstart', onUserTouch);
      userEl.addEventListener('pointerdown', onUserTouch);

      // save references for removal
      this.domListeners.push({ target: userEl, type: 'focus', fn: onUsernameFocus });
      this.domListeners.push({ target: userEl, type: 'touchstart', fn: onUserTouch });
      this.domListeners.push({ target: userEl, type: 'pointerdown', fn: onUserTouch });
      this.domListeners.push({ target: userEl, type: 'change', fn: () => this.syncInputValue('username') });
      // animationstart left as inline listener above
    }

    if (this.passwordInput?.nativeElement) {
      const pwdEl = this.passwordInput.nativeElement;
      pwdEl.addEventListener('change', () => {
        this.syncInputValue('password');
      });

      pwdEl.addEventListener('animationstart', (e: AnimationEvent) => {
        if (e.animationName === 'onAutoFillStart') {
          this.syncInputValue('password');
        }
      });

      const onPasswordFocus = () => {
        this.lastFocusedField = 'password';
      };
      pwdEl.addEventListener('focus', onPasswordFocus);

      const onPwdTouch = () => {
        this.lastFocusedField = 'password';
      };
      pwdEl.addEventListener('touchstart', onPwdTouch);
      pwdEl.addEventListener('pointerdown', onPwdTouch);

      this.domListeners.push({ target: pwdEl, type: 'focus', fn: onPasswordFocus });
      this.domListeners.push({ target: pwdEl, type: 'touchstart', fn: onPwdTouch });
      this.domListeners.push({ target: pwdEl, type: 'pointerdown', fn: onPwdTouch });
      this.domListeners.push({ target: pwdEl, type: 'change', fn: () => this.syncInputValue('password') });
    }

    // Global focusin gives a reliable picture of which element actually has focus,
    // including cases where the browser steals focus (iOS quirks).
    const onFocusIn = (ev: Event) => {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      if (this.usernameInput?.nativeElement === target) {
        this.lastFocusedField = 'username';
      } else if (this.passwordInput?.nativeElement === target) {
        this.lastFocusedField = 'password';
      } else {
        // leave lastFocusedField as-is for other elements
      }
    };
    document.addEventListener('focusin', onFocusIn);
    this.domListeners.push({ target: document, type: 'focusin', fn: onFocusIn });
  }

  private checkAndSyncAutocompleteValues(): void {
    // Verificar si los inputs tienen valores pero el formulario no
    if (this.usernameInput?.nativeElement.value && !this.loginForm.get('username')?.value) {
      this.syncInputValue('username');
    }
    
    if (this.passwordInput?.nativeElement.value && !this.loginForm.get('password')?.value) {
      this.syncInputValue('password');
    }
  }

  private syncInputValue(fieldName: 'username' | 'password'): void {
    const input = fieldName === 'username' ? this.usernameInput?.nativeElement : this.passwordInput?.nativeElement;
    
    if (input && input.value) {
      const value = fieldName === 'username' ? input.value.toLowerCase() : input.value;
      const control = this.loginForm.get(fieldName);
      if (control) {
        if (fieldName === 'password') {
          // Suppress focus-restore while we update the control programmatically
          this.suppressPasswordFocusRestore = true;
          try {
            control.setValue(value, { emitEvent: false });
          } finally {
            // Allow later restoration
            setTimeout(() => (this.suppressPasswordFocusRestore = false), 0);
          }
        } else {
          control.setValue(value, { emitEvent: false });
        }

        control.markAsTouched();
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  login(event?: Event) {
    // Prevenir que se dispare el submit si se hace clic fuera del formulario
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.loginForm.invalid) {
      Object.keys(this.loginForm.controls).forEach((key) => {
        const control = this.loginForm.get(key);
        control?.markAsTouched();
      });

      this.showToast('alert', 'Error', 'Debes rellenar todos los campos');

      return;
    }

    // Guardar o limpiar email recordado
    if (this.rememberSession) {
      localStorage.setItem('remembered_email', this.loginForm.value.username);
    } else {
      localStorage.removeItem('remembered_email');
    }

    this.authService
      .login(
        {
          email: this.loginForm.value.username,
          password: this.loginForm.value.password,
        },
        this.rememberSession
      )
      .subscribe({
        next: () => {
          // Verificar si realmente debemos ir a backoffice
          // Verificar query param, route data y session storage
          const hasBackofficeParam = this.route.snapshot.queryParams['backoffice'] === 'true';
          const hasBackofficeData = this.route.snapshot.data['backoffice'] === true;
          const hasBackofficeSession = sessionStorage.getItem('login_destination') === 'backoffice';
          const shouldGoToBackoffice = hasBackofficeParam || hasBackofficeData || hasBackofficeSession;
          
          if (shouldGoToBackoffice) {
            this.router.navigate(['/dashboard/home-backoffice']);
            // NO limpiar sessionStorage aún - el dashboard lo necesita
          } else {
            this.router.navigate(['/dashboard']);
            // Limpiar sessionStorage solo para accesos normales
            sessionStorage.removeItem('login_destination');
          }
        },
        error: (error) => {
          const errorMessage = error?.error?.message || 'Los datos introducidos son incorrectos';
          this.showToast(
            'alert',
            'Error',
            errorMessage
          );
        },
        complete: () => {},
      });
  }

  showToast(
    type: 'info' | 'success' | 'warning' | 'alert',
    title: string,
    message: string,
    duration: number = 4000
  ) {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.toastType = type;
    this.toastTitle = title;
    this.toastMessage = message;

    this.show = false;

    requestAnimationFrame(() => {
      this.show = true;

      this.toastTimer = setTimeout(() => {
        this.show = false;
        this.toastTimer = null;
      }, duration);
    });
  }

  loginWithMicrosoft(event?: Event) {
    // Prevenir cualquier validación del formulario principal
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    this.isMicrosoftLoginLoading = true;
    this.authService.loginMicrosoft().subscribe({
      next: (data) => {
        window.location.href = data.url
      },
      error: (error) => {
        this.isMicrosoftLoginLoading = false;
        const errorMessage = error?.error?.message || 'No se pudo iniciar sesión con Microsoft';
        this.showToast(
          'alert',
          'Error',
          errorMessage
        );
      },
      complete: () => {},
    });
  }

  handleTokenLogin(accessToken: string) {
    // Microsoft login: persistir sesión siempre (localStorage)
    localStorage.setItem('jwt_token', accessToken);

    this.authService.check().subscribe({
      next: () => {
        // Verificar si realmente debemos ir a backoffice
        // Verificar query param, route data y session storage
        const hasBackofficeParam = this.route.snapshot.queryParams['backoffice'] === 'true';
        const hasBackofficeData = this.route.snapshot.data['backoffice'] === true;
        const hasBackofficeSession = sessionStorage.getItem('login_destination') === 'backoffice';
        const shouldGoToBackoffice = hasBackofficeParam || hasBackofficeData || hasBackofficeSession;
        
        if (shouldGoToBackoffice) {
          this.router.navigate(['/dashboard/home-backoffice']);
          // NO limpiar sessionStorage aún - el dashboard lo necesita
        } else {
          this.router.navigate(['/dashboard']);
          // Limpiar sessionStorage solo para accesos normales
          sessionStorage.removeItem('login_destination');
        }
      },
      error: (error) => {
        this.isProcessingMicrosoftLogin = false;
        localStorage.removeItem('jwt_token');
        const errorMessage = error?.error?.message || 'El token de acceso no es válido o ha expirado';
        this.showToast(
          'alert',
          'Error',
          errorMessage
        );
      },
    });
  }

  // Convertir email a minúsculas automáticamente
  convertToLowercase(event: any): void {
    convertUsernameToLowercase(event, this.loginForm);
  }

  // Alternar visibilidad de la contraseña
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  ngOnDestroy(): void {
    try {
      this.subscriptions.unsubscribe();
    } catch (e) {
      // safe
    }
    
    // Remove any DOM listeners we added
    try {
      for (const l of this.domListeners) {
        try {
          l.target.removeEventListener(l.type, l.fn as EventListener);
        } catch (e) {
          // ignore errors per-listener
        }
      }
      this.domListeners = [];
    } catch (e) {
      // safe
    }
  }
}
